import { evaluateFormulaRaw, seedsAndEquations, variableDomain, type FormulaVariable } from '@agavi/blocks';

import { bracketizeChipsForFormula, bracketizeVariableChips, fmtValue, htmlHasVariableChips } from './variable-chip-preview';

// Plain-text extraction for rich content that may contain a math or reaction formula node
// (see math-formula-extension.ts / reaction-formula-extension.ts). Naively stripping HTML
// tags from the rendered KaTeX output mangles into extremely long, mostly-duplicated garbage:
// KaTeX renders both a MathML fallback (spaced-out token by token) and a visual glyph tree
// (also spaced-out once tags are stripped), so a blind regex strip produces the same formula
// three times over in one unreadable line. The original source (raw LaTeX, or the teacher's
// own typed reaction notation) is preserved untouched in a data attribute on the wrapping
// span — KaTeX only ever overwrites that span's inner HTML — so it can be lifted out directly
// instead of ever touching the rendered markup.

const FORMULA_LABEL_MAX_LENGTH = 40;

/** Truncates with a trailing "..." only when actually cutting text short — never appended to
 * text that already fits, per this app's standing rule against decorative ellipsis. */
function truncateForLabel(text: string, maxLength = FORMULA_LABEL_MAX_LENGTH): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}...`;
}

function stripTags(html: string): string {
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Decodes the standard HTML entities that `Element.innerHTML` re-introduces when serializing
 * a text node back to a string (e.g. a plain ">" character in a text node comes back out as
 * "&gt;" the moment anything reads .innerHTML again) — decoding &amp; last so it can't
 * re-trigger any of the other replacements. */
function decodeCommonEntities(text: string): string {
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/&amp;/g, '&');
}

export function htmlHasFormula(html: string): boolean {
  return html.includes('data-math-latex') || html.includes('data-reaction-formula') || html.includes('data-variable-chip');
}

/** Plain-text label for rich content that may include math/reaction formula nodes, or a variable
 * chip — swaps each for its own raw source (truncated only if it's actually long; a variable
 * chip becomes literal `{name}`, matching the convention every feedback-facing label follows —
 * chips are for the display card only, see `renderChipsForDisplay` in variable-chip-preview.tsx),
 * then strips ordinary tags/marks from what's left. Falls back to plain tag-stripping when
 * there's nothing to special-case, so this is a safe drop-in replacement everywhere a label is
 * built from rich text (dropdown/option labels, feedback titles, aria-labels).
 *
 * Variable chips specifically: the chip's OWN static HTML serialization (variable-chip-
 * extension.tsx's `renderHTML`) stores just the bare variable name as text content — the `{`/`}`
 * decoration exists only in the interactive editor's React node view and in
 * `renderChipsForDisplay`'s own display-time rewrite, neither of which this function goes
 * through. Without this special case, a blind tag-strip kept the chip's bare-name text node and
 * silently dropped the brackets — a label built from option text containing a variable read as
 * the bare name, not `{name}`, in every feedback/Flow context. */
export function stripHtmlForLabel(html: string): string {
  if (!htmlHasFormula(html)) return stripTags(html);
  const div = document.createElement('div');
  div.innerHTML = bracketizeVariableChips(html);
  div.querySelectorAll<HTMLElement>('[data-math-latex]').forEach(el => {
    el.replaceWith(document.createTextNode(truncateForLabel(el.getAttribute('data-math-latex') ?? '')));
  });
  div.querySelectorAll<HTMLElement>('[data-reaction-formula]').forEach(el => {
    el.replaceWith(document.createTextNode(truncateForLabel(el.getAttribute('data-reaction-formula') ?? '')));
  });
  // div.innerHTML re-serializes the text nodes just substituted above, re-escaping characters
  // like ">" that a formula's raw source commonly contains (e.g. a reaction arrow "->") right
  // back into "&gt;" — undoing the entire point of extracting clean plain text. Decoding is
  // the final step, after normal tag-stripping, since only text should ever remain by then.
  return decodeCommonEntities(stripTags(div.innerHTML));
}

/**
 * AUTHORED PROSE, AS A FORMULA — what the "options as equations" toggle turns an option into.
 *
 * `stripHtmlForLabel` is the LABEL converter: a variable becomes `{name}` because every
 * feedback-facing label follows that convention, and a recall is left to strip down to the words
 * it was last rendered as, which is what a title wants. A FORMULA wants neither — it stores a
 * recall as `[ara_…]`, and the label path quietly turned a reference into a word.
 *
 * One function so both blocks convert the same way: MCQ and Dropdown each had their own copy of
 * the toggle and this is the line they both got wrong.
 */
export function richTextToFormula(
  html: string, numericAliasIds: ReadonlySet<string> = new Set(),
): string {
  return stripHtmlForLabel(bracketizeChipsForFormula(html, numericAliasIds));
}

/**
 * Is there anything in here a reader would see?
 *
 * Built on `stripHtmlForLabel` rather than a tag-strip, which matters: a scenario or a text
 * block holding nothing but a math formula or a variable chip has no ordinary text nodes at
 * all, and the regex this replaces — `html.replace(/<[^>]+>/g, '')`, written inline in
 * text-block — read it as empty. A block whose whole content is an equation is not empty.
 *
 * `&nbsp;` counts as whitespace, since a teacher who pressed space into an empty editor has
 * authored nothing.
 */
export function htmlHasContent(html: string | undefined): boolean {
  if (!html) return false;
  // BOTH spellings. `stripTags` leaves the literal `&nbsp;` in place for ordinary html, while
  // the formula path decodes it to U+00A0 first — so checking one of them passes blank content
  // through half the time, which is how the original inline regex got it right by only ever
  // meeting the literal.
  return stripHtmlForLabel(html).replace(/&nbsp;/g, ' ').replace(/\u00a0/g, ' ').trim().length > 0;
}

/**
 * Plain text for a hover tooltip, whose job is to reveal content the layout has visually clipped.
 *
 * Deliberately NOT `stripHtmlForLabel`. That substitutes a formula's raw source, which is right for
 * a chip a teacher scans but turns a tooltip into noise: `\sum_0^{\infty}xdx+5x+1` reveals nothing
 * a reader wants. The rendered form is no better as text — KaTeX's own MathML flattens
 * `\frac{1}{2}mv^2` to "12mv2", which reads as twelve, and mhchem leaves stray "X" separators
 * ("CaX2++2 ClX−"). A wrong tooltip is worse than none.
 *
 * So: a REACTION keeps its source, because mhchem's syntax is the notation a science teacher already
 * writes and reads ("2H2 + O2 -> 2H2O"). A MATH formula is dropped, because its source is markup,
 * not notation. Prose around either survives, which is the case where a tooltip earns its keep.
 *
 * Returns '' when nothing readable is left — the caller should then render no `title` at all rather
 * than an empty tooltip.
 */
export function stripHtmlForTooltip(html: string): string {
  if (!htmlHasFormula(html)) return stripTags(html);
  const div = document.createElement('div');
  div.innerHTML = bracketizeVariableChips(html);
  div.querySelectorAll<HTMLElement>('[data-math-latex]').forEach(el => { el.remove(); });
  div.querySelectorAll<HTMLElement>('[data-reaction-formula]').forEach(el => {
    el.replaceWith(document.createTextNode(el.getAttribute('data-reaction-formula') ?? ''));
  });
  return decodeCommonEntities(stripTags(div.innerHTML));
}

// ── Chip-value collision detection ──────────────────────────────────────────
// A sibling check to `checkOptionFormulaIssues` (packages/blocks/dropdown.ts) for the OTHER shape
// variable chips take in an option: a plain-text option (never `optionsAsEquations`) with one or
// more chips embedded in otherwise-fixed text, e.g. "Mama {n}" / "Mama {m}". Two such options
// stay visually distinct as long as `{n}` and `{m}` never roll to the same value — but nothing
// stopped a teacher from giving them overlapping ranges, so on some fraction of rolls a student
// could see "Mama 5" / "Mama 5" and have no way to tell the options apart. This is a genuinely
// different bug than `checkOptionFormulaIssues` catches: that one only ever looks at FULL-formula
// options (`optionsAsEquations` on), this one looks at plain option TEXT with chips inside it —
// same fairness hazard, different data shape, so it needed its own brute-force check rather than
// being folded into the existing one.

/** A chip's static HTML serialization is always `<span data-variable-chip="name">name</span>`
 * (variable-chip-extension.tsx's `renderHTML`) — bare name as both the attribute and the text
 * content, never nested markup. Substituting via this regex instead of `processVariableChipHTML`
 * (variable-chip-preview.tsx) is deliberate: that function does real DOM `innerHTML` parsing per
 * call, fine for the handful of times it runs per render, but this check calls its substitution
 * step up to `MAX_CHIP_COLLISION_COMBINATIONS` times — DOM churn at that volume would make the
 * check noticeably slow. A plain regex replace is equivalent here (the chip's own content is
 * always just its bare name, nothing a regex could get wrong) and orders of magnitude cheaper. */
const CHIP_SPAN_REGEX = /<span[^>]*\bdata-variable-chip="([^"]*)"[^>]*>[^<]*<\/span>/g;

export interface OptionChipCollisionIssue {
  /** Exactly two (sorted) — mirrors OptionFormulaIssue's own contract in packages/blocks. */
  optionIds: string[];
}

/** Same cap as packages/blocks' MAX_OPTION_FORMULA_COMBINATIONS, same reasoning: a question
 * referencing several wide-range variables could otherwise enumerate combinatorially too many
 * samples — capped, not skipped, so the check still runs on a bounded sample. */
export const MAX_CHIP_COLLISION_COMBINATIONS = 5000;

/**
 * Brute-force-checks every PLAIN-TEXT option (i.e. `optionsAsEquations` off, or a block with no
 * such toggle at all) for two options whose rendered text could become identical for some
 * in-range combination of the variables their chips reference — across the full declared range
 * of each variable, exact enumeration (Agavi variables always have a declared min/max/step, so
 * the domain is finite), same shape as `checkOptionFormulaIssues`.
 *
 * Deliberately compares POST-`stripHtmlForLabel` plain text, not raw HTML — two options that
 * happen to render the identical resolved text but differ only in formatting (one bold, one not)
 * are a separate, not-yet-built concern (label-collision disambiguation for export), not this
 * check's job; this one is specifically about a student being unable to tell two options apart at
 * all, which formatting differences alone don't cause. Also skips any pair that's already an
 * exact raw-HTML duplicate (caught by the block's own dedicated duplicate-detection) so a teacher
 * doesn't see the same root cause reported twice under two different messages.
 */
/**
 * A RECALL CHIP'S POSSIBLE DISPLAYED VALUES, keyed by alias id.
 *
 * The other thing an option can embed that varies per student. A recall shows whatever the
 * student answered earlier, so two options that differ only by a recall can read identically
 * for whoever gave the matching answer — the same unfairness a variable collision is, through
 * a different door.
 *
 * Supplied by the caller rather than computed here, because the values come from ANOTHER
 * TASK's block and this module knows nothing about tasks. `recallOptionSpaces` builds it.
 *
 * An alias missing from the map is left as an opaque token and simply never matches, which is
 * the honest failure: a keyword or numerical source does not enumerate, so a collision through
 * one cannot be detected at all. Partial coverage, by design — see the phase-3 scope.
 */
export type RecallValueSpaces = Record<string, string[]>;

/** `data-answer-ref-alias` spans, the saved form of a recall chip — the alias-id attribute is
 *  what identifies it, exactly as `answer-ref-alias.ts`'s own scans match on. */
const REF_SPAN_REGEX = /<span[^>]*\bdata-answer-ref-alias="([^"]*)"[^>]*>[^<]*<\/span>/g;

export function checkOptionChipCollisions(
  options: { id: string; text: string; media?: unknown }[],
  // `FormulaVariable`, not a narrower literal: a plain-text option can embed a chip for a constant
  // or an equation just as easily as for a random, and a collision between two of them is exactly
  // as unfair to a student.
  variables: FormulaVariable[],
  /**
   * What each recall chip in these options could show. Omitted where the caller has no recall
   * scope, which leaves the check exactly as it was.
   *
   * Swept TOGETHER with the variables rather than in a second pass: an option holding both a
   * chip and a recall collides on the cross product, and two checks each sweeping their own
   * axis would both miss it.
   */
  recalls: RecallValueSpaces = {},
): { issues: OptionChipCollisionIssue[]; truncated: boolean } {
  const candidates = options.filter(o => !o.media);
  const refIds = new Set<string>();
  for (const o of candidates) {
    for (const m of o.text.matchAll(REF_SPAN_REGEX)) {
      if (recalls[m[1] ?? '']) refIds.add(m[1] ?? '');
    }
  }
  if (candidates.length < 2
    || (!candidates.some(o => htmlHasVariableChips(o.text)) && refIds.size === 0)) {
    return { issues: [], truncated: false };
  }

  const referencedNames = new Set<string>();
  for (const o of candidates) {
    for (const m of o.text.matchAll(CHIP_SPAN_REGEX)) referencedNames.add(m[1] ?? '');
  }
  /*
   * A plain-text option can embed a chip for a CONSTANT or an EQUATION, not only for a random, so
   * what gets enumerated is the seeds the chips ultimately stand on. The old filter demanded a
   * finite min/max/step, which dropped a constant entirely — its name then never appeared in a
   * combination and the chip was simply never compared, silently missing the collision.
   *
   * `blocked` is ignored: a chip naming something undefined is the block's own problem to report, and
   * leaving it out of the sweep keeps this check from inventing a second verdict about it.
   */
  const { seeds: relevantVars, equations } = seedsAndEquations(referencedNames, variables);

  let combos: Record<string, number>[] = [{}];
  let truncated = false;
  for (const v of relevantVars) {
    const domain = variableDomain(v);
    if (domain.length === 0) continue;
    const next: Record<string, number>[] = [];
    outer: for (const combo of combos) {
      for (const value of domain) {
        if (next.length >= MAX_CHIP_COLLISION_COMBINATIONS) { truncated = true; break outer; }
        next.push({ ...combo, [v.name]: value });
      }
    }
    combos = next;
    if (truncated) break;
  }

  // Equations are computed per combination, in dependency order, so a chip for one resolves to the
  // number a student would see for that roll — which is the only thing a collision can be about.
  for (const combo of combos) {
    for (const eq of equations) {
      const scope: Record<string, string> = {};
      for (const [name, value] of Object.entries(combo)) scope[name] = String(value);
      const computed = evaluateFormulaRaw(eq.formula!, scope);
      if (isFinite(computed)) combo[eq.name] = computed;
    }
  }

  // Precomputed once, outside the per-combo loop below — a pair's raw-HTML identity never
  // depends on which combo is currently being sampled.
  const alreadyExactDuplicate = new Set<string>();
  for (let i = 0; i < candidates.length; i++) {
    for (let j = i + 1; j < candidates.length; j++) {
      if (candidates[i]!.text === candidates[j]!.text) {
        alreadyExactDuplicate.add([candidates[i]!.id, candidates[j]!.id].sort().join('|'));
      }
    }
  }

  /*
   * EVERY VARIABLE COMBINATION CROSSED WITH EVERY RECALL COMBINATION, under the one cap.
   *
   * The cross product is the point: an option reading "{n} on Mars" against one reading
   * "5 on [planet]" collides only when BOTH land right, and sweeping the two axes separately
   * would clear the pair twice while the collision sat in the product nobody looked at.
   */
  let refCombos: Record<string, string>[] = [{}];
  for (const aliasId of refIds) {
    const next: Record<string, string>[] = [];
    outerRefs: for (const combo of refCombos) {
      for (const value of recalls[aliasId]!) {
        if (next.length >= MAX_CHIP_COLLISION_COMBINATIONS) { truncated = true; break outerRefs; }
        next.push({ ...combo, [aliasId]: value });
      }
    }
    refCombos = next;
    if (truncated) break;
  }

  const collisionPairs = new Set<string>();
  for (const combo of combos) {
    const resolvedValues = Object.fromEntries(Object.entries(combo).map(([name, n]) => [name, fmtValue(n)]));
    for (const refCombo of refCombos) {
      const resolved = candidates.map(o => ({
        id: o.id,
        text: stripHtmlForLabel(o.text
          .replace(CHIP_SPAN_REGEX, (_m, name: string) => resolvedValues[name] ?? `{${name}}`)
          /* An alias with no enumerable space keeps its own token, so it matches only another
             copy of itself — which is the exact-duplicate case, already reported elsewhere. */
          .replace(REF_SPAN_REGEX, (m, id: string) => refCombo[id] ?? m)),
      }));
      for (let i = 0; i < resolved.length; i++) {
        for (let j = i + 1; j < resolved.length; j++) {
          const a = resolved[i]!;
          const b = resolved[j]!;
          if (!a.text || a.text !== b.text) continue;
          const key = [a.id, b.id].sort().join('|');
          if (alreadyExactDuplicate.has(key)) continue; // already flagged elsewhere
          collisionPairs.add(key);
        }
      }
    }
  }

  return {
    issues: Array.from(collisionPairs).map(pair => ({ optionIds: pair.split('|') })),
    truncated,
  };
}
